/* eslint-disable react/no-array-index-key */
import React from "react";

import { faChevronDown, faChevronRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import cx from "classnames";
import PropTypes from "prop-types";

import store from "~/lib/store";
import { IconButton } from "~/ui/legacyFellowIndex";

import DateContentRow from "./DateContentRow";
import Header from "./Header";
import ResourceHeader from "./ResourceHeader";

const HIDE_ALL_DAY_EVENTS_STORE_KEY = "hideAllDayEvents";

class TimeGridHeader extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			hideAllDayEvents: Boolean(store.get(HIDE_ALL_DAY_EVENTS_STORE_KEY)),
		};
	}

	renderHeaderCells(range) {
		let {
			localizer,
			getNow,
			getters: { dayProp },
		} = this.props;

		const today = getNow();

		return range.map((date, i) => {
			const { className, style } = dayProp(date);

			return (
				<div
					key={i}
					style={style}
					className={cx("fbc-header", className, { "fbc-today": localizer.isSameDate(date, today) })}
				>
					<Header date={date} getNow={getNow} showDay />
				</div>
			);
		});
	}

	toggleAllDaySection = () => {
		this.setState({ hideAllDayEvents: !this.state.hideAllDayEvents });
		store.set(HIDE_ALL_DAY_EVENTS_STORE_KEY, !this.state.hideAllDayEvents);
	};

	render() {
		let {
			widths,
			rtl,
			resources,
			range,
			events,
			getNow,
			accessors,
			selectable,
			components,
			getters,
			scrollRef,
			localizer,
			isOverflowing,
			components: { resourceHeader: ResourceHeaderComponent = ResourceHeader },
		} = this.props;

		const groupedEvents = resources.groupEvents(events);
		const showAllEventsSectionToggle = Array.from(groupedEvents.values()).flat().length !== 0;

		const { preferredTimezone, secondTimezone, thirdTimezone } = this.props.timezones;

		return (
			<div ref={scrollRef} className={cx("fbc-time-header", { "fbc-overflowing": isOverflowing })}>
				<div className="fbc-label fbc-time-header-gutter">
					<div className="fbc-time-header-gutter-timezones">
						<span style={{ width: widths[0], minWidth: widths[0], maxWidth: widths[0] }}>
							{preferredTimezone.label}
						</span>
						{secondTimezone && (
							<span style={{ width: widths[1], minWidth: widths[1], maxWidth: widths[1] }}>{secondTimezone.label}</span>
						)}
						{thirdTimezone && (
							<span style={{ width: widths[2], minWidth: widths[2], maxWidth: widths[2] }}>{thirdTimezone.label}</span>
						)}
					</div>
					{showAllEventsSectionToggle && (
						<div className="fbc-time-header-gutter-allday-row">
							<IconButton
								icon={this.state.hideAllDayEvents ? faChevronRight : faChevronDown}
								className="fbc-allday-row-arrow-toggle"
								onClick={this.toggleAllDaySection}
							/>
						</div>
					)}
				</div>

				{resources.map(([id, resource], idx) => (
					<div className="fbc-time-header-content" key={id || idx}>
						{resource && (
							<div className="fbc-row fbc-row-resource" key={`resource_${idx}`}>
								<div className="fbc-header">
									<ResourceHeaderComponent index={idx} label={accessors.resourceTitle(resource)} resource={resource} />
								</div>
							</div>
						)}
						<div
							className={`fbc-row fbc-time-header-cell${range.length <= 1 ? " fbc-time-header-cell-single-day" : ""}`}
						>
							{this.renderHeaderCells(range)}
						</div>
						<DateContentRow
							isAllDay
							hideAllDayEvents={this.state.hideAllDayEvents}
							toggleHideAllDayEvents={this.toggleAllDaySection}
							rtl={rtl}
							getNow={getNow}
							minRows={2}
							range={range}
							events={groupedEvents.get(id) || []}
							resourceId={resource && id}
							className="fbc-allday-cell"
							selectable={selectable}
							selected={this.props.selected}
							components={components}
							accessors={accessors}
							getters={getters}
							localizer={localizer}
							onSelect={this.props.onSelectEvent}
							onDoubleClick={this.props.onDoubleClickEvent}
							onKeyPress={this.props.onKeyPressEvent}
							onSelectSlot={this.props.onSelectSlot}
							longPressThreshold={this.props.longPressThreshold}
						/>
					</div>
				))}
			</div>
		);
	}
}

TimeGridHeader.propTypes = {
	range: PropTypes.array.isRequired,
	events: PropTypes.array.isRequired,
	resources: PropTypes.object,
	getNow: PropTypes.func.isRequired,
	isOverflowing: PropTypes.bool,

	rtl: PropTypes.bool,
	widths: PropTypes.array,

	localizer: PropTypes.object.isRequired,
	accessors: PropTypes.object.isRequired,
	components: PropTypes.object.isRequired,
	getters: PropTypes.object.isRequired,

	selected: PropTypes.object,
	selectable: PropTypes.oneOf([true, false, "ignoreEvents"]),
	longPressThreshold: PropTypes.number,

	onSelectSlot: PropTypes.func,
	onSelectEvent: PropTypes.func,
	onDoubleClickEvent: PropTypes.func,
	onKeyPressEvent: PropTypes.func,
	onDrillDown: PropTypes.func,
	getDrilldownView: PropTypes.func.isRequired,
	scrollRef: PropTypes.any,

	/** Timezone information for the time axis labels */
	timezones: PropTypes.object,
};

export default TimeGridHeader;
